import { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  Send,
  RotateCcw,
  ArrowRight,
  Check,
  Sparkles,
  Activity,
  TrendingUp,
  MessageCircle,
  Globe,
  Instagram,
  Phone,
} from "lucide-react";
import type { DemoFormData } from "../demoTypes";
import type { DemoFlow, DemoFlowCtaStep, DemoFlowActionSetAction } from "../flowTypes";
import { buildCtaUrl, resolveActionTarget } from "../flowBuilder";
import type { CtaDestination } from "../sectorConfigs";
import type { SignalCardData } from "../demoSignal";
import DemoCompletion from "./DemoCompletion";
import { useDemoSimulator, type ChatMessage } from "../useDemoSimulator";

export default function DemoSimulator({
  data,
  flow,
  onEdit,
  editLabel = "Demoyu Düzenle",
  completionEditLabel = "Bilgilerimi Düzenle",
  onCreateCustom,
}: {
  data: DemoFormData;
  flow: DemoFlow;
  onEdit: () => void;
  editLabel?: string;
  completionEditLabel?: string;
  onCreateCustom?: () => void;
}) {
  const sim = useDemoSimulator(flow, data);
  const [input, setInput] = useState("");
  const viewportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = viewportRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [sim.messages, sim.isTyping, sim.currentChoice, sim.completed, sim.handoffAction]);

  const submit = () => {
    const val = input.trim();
    if (!val) return;
    sim.send(val);
    setInput("");
  };

  const inputDisabled = sim.inputMode === "disabled";
  const placeholder =
    sim.inputMode === "start"
      ? "MERHABA yazın…"
      : sim.inputMode === "question"
        ? "Cevabınızı yazın…"
        : "…";

  return (
    <>
      <style>{`
        @keyframes signalIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes signalOut {
          from { opacity: 1; transform: translateY(0); }
          to { opacity: 0; transform: translateY(-6px); }
        }
        .signal-card-in { animation: signalIn 0.3s ease-out both; }
        .signal-card-out { animation: signalOut 0.3s ease-in both; }
      `}</style>

      <div className="min-h-screen bg-primary-950 px-4 py-6 text-background-50">
        <div className="mx-auto max-w-md">
          {/* Üst aksiyon barı */}
          <div className="mb-4 flex items-center justify-between">
            <button
              onClick={onEdit}
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium text-background-300 transition hover:text-background-50"
            >
              <ArrowLeft className="h-4 w-4" />
              {editLabel}
            </button>
            <span className="font-heading text-sm font-bold tracking-tight text-background-100">
              SMART DEMO
            </span>
            <button
              onClick={sim.reset}
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium text-background-300 transition hover:text-background-50"
            >
              <RotateCcw className="h-4 w-4" />
              Yeniden Başlat
            </button>
          </div>

          {/* Telefon mockup */}
          <div className="relative flex h-[72vh] min-h-[520px] flex-col overflow-hidden rounded-[2.5rem] border-4 border-background-800 bg-background-50 text-foreground-900">
            {/* Sinyal kartı — header altında, tek kart, non-blocking */}
            {sim.signalCard && (
              <div className="pointer-events-none absolute left-3 right-3 top-16 z-30">
                <div className={sim.signalLeaving ? "signal-card-out" : "signal-card-in"}>
                  <SignalCardView card={sim.signalCard} />
                </div>
              </div>
            )}

            {/* Header — Instagram DM hissi */}
            <div className="flex items-center justify-between gap-2 border-b border-background-200 bg-background-50 px-3 py-2.5">
              <div className="flex min-w-0 items-center gap-2.5">
                {data.logo ? (
                  <img
                    src={data.logo}
                    alt={data.firmaAdi || "logo"}
                    className="h-9 w-9 rounded-full object-cover object-top"
                  />
                ) : (
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-500 text-base font-bold text-primary-950">
                    {(data.firmaAdi || "İ").charAt(0).toUpperCase()}
                  </span>
                )}
                <div className="min-w-0 leading-tight">
                  <span className="block truncate text-sm font-semibold text-foreground-900">
                    {data.firmaAdi || "İşletmeniz"}
                  </span>
                  <span className="flex items-center gap-1 text-[10px] text-foreground-500">
                    <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
                    çevrimiçi
                  </span>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <span className="hidden text-[9px] font-medium text-foreground-400 sm:block">
                  Demo Modu
                </span>
                <span
                  className={`flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-[10px] font-semibold transition-all duration-300 ${
                    sim.scorePulse
                      ? "scale-105 bg-accent-500 text-primary-950"
                      : "bg-background-200 text-foreground-700"
                  }`}
                >
                  <Activity className="h-3 w-3" />
                  Potansiyel: {sim.signal.score}
                </span>
              </div>
            </div>

            {/* Sohbet gövdesi */}
            <div
              ref={viewportRef}
              className="flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-4"
            >
              {!sim.started && (
                <>
                  <div className="max-w-[90%] self-center rounded-full bg-background-200 px-4 py-2 text-center text-xs text-foreground-500">
                    Şimdi kendinizi reklamınızdan gelen bir müşteri gibi düşünün.
                  </div>
                  <div className="max-w-[90%] self-center rounded-full bg-background-200 px-4 py-2 text-center text-xs text-foreground-500">
                    Başlamak için aşağıya MERHABA yazın.
                  </div>
                </>
              )}

              {sim.messages.map((m) => (
                <MessageBubble key={m.id} msg={m} data={data} />
              ))}

              {sim.isTyping && (
                <div className="flex items-center gap-1 self-start rounded-2xl rounded-tl-md bg-background-100 px-4 py-3">
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      className="h-1.5 w-1.5 animate-bounce rounded-full bg-foreground-400"
                      style={{ animationDelay: `${i * 150}ms` }}
                    />
                  ))}
                </div>
              )}

              {sim.completed && sim.finalActions && !sim.handoffDone && (
                <div className="mt-2 flex w-full flex-col items-center gap-3">
                  {sim.handoffAction ? (
                    <DestinationSimulationCard
                      action={sim.handoffAction}
                      data={data}
                      onComplete={sim.completeHandoff}
                    />
                  ) : (
                    <ActionSetAnchor actions={sim.finalActions} onClick={sim.registerActionClick} />
                  )}
                </div>
              )}

              {sim.completed && sim.finalCta && (
                <div className="mt-2 flex flex-col items-center gap-3">
                  <CtaAnchor step={sim.finalCta} data={data} onClick={sim.registerCtaClick} />
                  <div className="rounded-full bg-background-200 px-4 py-1.5 text-xs font-medium text-foreground-500">
                    ✓ Demo tamamlandı
                  </div>
                </div>
              )}
            </div>

            {/* Alt alan: quick reply veya input */}
            {sim.currentChoice ? (
              <div className="flex flex-wrap gap-2 border-t border-background-200 px-3 py-3">
                {sim.currentChoice.options.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => sim.choose(opt)}
                    className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition hover:border-accent-400 hover:bg-accent-50 active:scale-95"
                  >
                    {opt}
                  </button>
                ))}
              </div>
            ) : sim.awaitingContinue ? (
              <div className="border-t border-background-200 p-3">
                <button
                  onClick={sim.continueFlow}
                  className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600"
                >
                  Devam Et
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="border-t border-background-200 px-3 py-3">
                {sim.hint && (
                  <p className="mb-2 px-1 text-xs font-medium text-accent-700">{sim.hint}</p>
                )}
                <div className="flex items-center gap-2">
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") submit();
                    }}
                    disabled={inputDisabled}
                    placeholder={placeholder}
                    className="flex-1 rounded-full bg-background-200 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition focus:bg-background-100 disabled:cursor-not-allowed disabled:opacity-60"
                  />
                  <button
                    onClick={submit}
                    disabled={inputDisabled || !input.trim()}
                    className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {sim.handoffAction && !sim.handoffDone && (
            <div className="fixed inset-x-0 bottom-0 z-40 bg-gradient-to-t from-primary-950 via-primary-950/95 to-transparent pt-6 pb-[calc(0.875rem+env(safe-area-inset-bottom))] md:hidden">
              <div className="mx-auto max-w-md px-4">
                <button
                  onClick={sim.completeHandoff}
                  className="w-full cursor-pointer whitespace-nowrap rounded-full bg-primary-500 py-3.5 text-sm font-semibold text-background-50 transition hover:bg-primary-600"
                >
                  Demo Sonuçlarını Gör
                </button>
              </div>
            </div>
          )}

          {sim.completed && (sim.finalCta ? true : sim.handoffDone) && (
            <DemoCompletion
              flow={flow}
              signal={sim.signal}
              data={data}
              handoffAction={sim.handoffAction}
              onRestart={sim.reset}
              onEdit={onEdit}
              onEditLabel={completionEditLabel}
              onCreateCustom={onCreateCustom}
            />
          )}

          <p className="mx-auto mt-5 max-w-sm text-center text-xs leading-relaxed text-background-400">
            Bu demo, işletmeniz için verdiğiniz bilgiler kullanılarak özel olarak oluşturuldu.
          </p>
        </div>

        {/* Davranış sinyali açıklayıcı kartı — 3 anlamlı event sonrası bir kez */}
        {sim.showExplainer && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/50 px-4 py-8"
            onClick={sim.dismissExplainer}
          >
            <div
              className="animate-fade-in-up w-full max-w-sm rounded-2xl border border-background-200 bg-background-50 p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                <Sparkles className="h-5 w-5" />
              </div>
              <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">
                Sistem davranışı analiz etmeye başladı
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                Bir müşteri yalnız mesaj yazmakla kalmaz. Hangi hizmeti seçtiği, hangi kampanyayı
                incelediği, hangi görselleri görüntülediği ve iletişim aşamasına kadar ilerleyip
                ilerlemediği de ölçülebilir.
              </p>
              <p className="mt-3 text-xs leading-relaxed text-foreground-400">
                Bu davranışlar gerçek kurulumda uygun ölçüm event'lerine dönüştürülebilir ve reklam
                optimizasyonuna veri sağlayabilir.
              </p>
              <button
                onClick={sim.dismissExplainer}
                className="mt-5 w-full cursor-pointer whitespace-nowrap rounded-full bg-primary-500 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600"
              >
                Devam Et
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function SignalCardView({ card }: { card: SignalCardData }) {
  const isLevel = card.kind === "levelup";

  return (
    <div className="rounded-xl border border-accent-500/40 bg-primary-950/95 p-3 shadow-xl backdrop-blur">
      <div className="flex items-start gap-2.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-500 text-primary-950">
          {isLevel ? (
            <TrendingUp className="h-4 w-4" strokeWidth={2.5} />
          ) : (
            <Activity className="h-4 w-4" strokeWidth={2.5} />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-xs font-bold tracking-wide text-background-50">
              {card.title}
            </span>
            {isLevel ? (
              <span className="shrink-0 rounded-full bg-accent-500/20 px-2 py-0.5 text-[11px] font-bold text-accent-300">
                {card.description}
              </span>
            ) : (
              <span className="shrink-0 rounded-full bg-accent-500 px-2 py-0.5 text-xs font-bold text-primary-950">
                +{card.points}
              </span>
            )}
          </div>
          {!isLevel && (
            <p className="mt-0.5 text-[11px] leading-snug text-background-300">
              {card.description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ msg, data }: { msg: ChatMessage; data: DemoFormData }) {
  const isCustomer = msg.sender === "customer";

  if (msg.type === "branded_welcome") {
    if (!data.logo) {
      return (
        <div className="max-w-[85%] self-start whitespace-pre-wrap rounded-2xl rounded-tl-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-900">
          {msg.content}
        </div>
      );
    }
    return (
      <div className="flex max-w-[85%] flex-col items-center self-start rounded-2xl rounded-tl-md border border-background-200 bg-background-50 px-5 py-5">
        <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-background-200 bg-background-100 p-2.5">
          <img
            src={data.logo}
            alt={data.firmaAdi || "Firma logosu"}
            className="h-full w-full object-contain"
          />
        </div>
        {data.firmaAdi && (
          <span className="mt-2.5 text-sm font-bold text-foreground-900">{data.firmaAdi}</span>
        )}
        <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-foreground-700">
          {msg.content}
        </p>
      </div>
    );
  }

  if (msg.type === "gallery") {
    return <GalleryMessage msg={msg} />;
  }

  if (msg.type === "campaign_list") {
    return <CampaignListMessage msg={msg} />;
  }

  if (msg.type === "image" || msg.type === "video") {
    const src =
      msg.type === "image"
        ? data.gorseller[msg.mediaIndex ?? 0]
        : data.videolar[msg.mediaIndex ?? 0];
    if (!src) return null;
    return <MediaMessage type={msg.type} src={src} caption={msg.content} />;
  }

  return (
    <div
      className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm ${
        isCustomer
          ? "self-end rounded-tr-md bg-accent-500 text-primary-950"
          : "self-start rounded-tl-md border border-background-200 bg-background-100 text-foreground-900"
      }`}
    >
      {msg.content}
    </div>
  );
}

function MediaMessage({
  type,
  src,
  caption,
}: {
  type: "image" | "video";
  src: string;
  caption?: string;
}) {
  const [loaded, setLoaded] = useState(false);

  return (
    <div className="max-w-[80%] self-start">
      {!loaded && (
        <div className="flex h-48 w-56 animate-pulse items-center justify-center rounded-2xl bg-background-200">
          <span className="text-xs text-foreground-400">Yükleniyor…</span>
        </div>
      )}
      {type === "image" ? (
        <img
          src={src}
          alt="Görsel"
          onLoad={() => setLoaded(true)}
          className={`w-full rounded-2xl object-cover object-top ${loaded ? "block" : "hidden"}`}
        />
      ) : (
        <video
          src={src}
          controls
          muted
          playsInline
          onLoadedData={() => setLoaded(true)}
          className={`w-full rounded-2xl ${loaded ? "block" : "hidden"}`}
        />
      )}
      {caption && <p className="mt-1 text-xs text-foreground-500">{caption}</p>}
    </div>
  );
}

function CtaAnchor({
  step,
  data,
  onClick,
}: {
  step: DemoFlowCtaStep;
  data: DemoFormData;
  onClick: () => void;
}) {
  const url = buildCtaUrl(step.channel, data);
  const isTel = step.channel === "telefon";
  const channelName =
    step.channel === "instagram" ? "Instagram" : step.channel === "telefon" ? "telefon" : "WhatsApp";

  if (!url) {
    return (
      <div className="w-full max-w-[240px] rounded-full bg-background-200 px-4 py-2 text-center text-xs text-foreground-400">
        İletişim bilgisi bulunamadı
      </div>
    );
  }

  return (
    <div className="flex w-full max-w-[240px] flex-col gap-1.5">
      <a
        href={url}
        target={isTel ? undefined : "_blank"}
        rel={isTel ? undefined : "nofollow noreferrer"}
        onClick={onClick}
        className="block w-full whitespace-nowrap rounded-full bg-primary-500 py-3 text-center text-sm font-semibold text-background-50 transition hover:bg-primary-600"
      >
        {step.label}
      </a>
      <p className="text-center text-[10px] leading-relaxed text-foreground-400">
        Gerçek müşterileriniz bu noktada {channelName} hattınıza yönlendirilir.
      </p>
    </div>
  );
}

function ActionSetAnchor({
  actions,
  onClick,
}: {
  actions: DemoFlowActionSetAction[];
  onClick: (action: DemoFlowActionSetAction) => void;
}) {
  return (
    <div className="flex w-full max-w-[260px] flex-col gap-2">
      {actions.map((a, i) => (
        <button
          key={a.id || i}
          onClick={() => onClick(a)}
          className={`block w-full cursor-pointer whitespace-nowrap rounded-full py-3 text-center text-sm font-semibold transition ${
            i === 0
              ? "bg-primary-500 text-background-50 hover:bg-primary-600"
              : "border border-primary-300 bg-background-50 text-primary-700 hover:bg-primary-50"
          }`}
        >
          {a.label}
        </button>
      ))}
      <p className="text-center text-[10px] leading-relaxed text-foreground-400">
        Gerçek müşterileriniz bu noktada iletişim kanalınıza yönlendirilir.
      </p>
    </div>
  );
}

const DESTINATION_COPY: Record<
  CtaDestination,
  { title: string; description: string; targetLabel: string; channelName: string }
> = {
  whatsapp: {
    title: "WhatsApp yönlendirmesi hazır",
    description: "Gerçek sistemde müşteri bu noktada işletmenizin WhatsApp hattına yönlendirilir.",
    targetLabel: "Hedef numara",
    channelName: "WhatsApp",
  },
  website: {
    title: "Web sitesi yönlendirmesi hazır",
    description: "Gerçek sistemde müşteri bu noktada web sitenize / mağazanıza yönlendirilir.",
    targetLabel: "Hedef adres",
    channelName: "web sitesi",
  },
  instagram: {
    title: "Instagram yönlendirmesi hazır",
    description: "Gerçek sistemde müşteri bu noktada Instagram hesabınıza yönlendirilir.",
    targetLabel: "Hedef hesap",
    channelName: "Instagram",
  },
  phone: {
    title: "Telefon yönlendirmesi hazır",
    description: "Gerçek sistemde müşteri bu noktada işletmenizi doğrudan arayabilir.",
    targetLabel: "Hedef numara",
    channelName: "telefon",
  },
};

function DestinationIcon({ destination }: { destination: CtaDestination }) {
  if (destination === "website") return <Globe className="h-4 w-4" strokeWidth={2.5} />;
  if (destination === "instagram") return <Instagram className="h-4 w-4" strokeWidth={2.5} />;
  if (destination === "phone") return <Phone className="h-4 w-4" strokeWidth={2.5} />;
  return <MessageCircle className="h-4 w-4" strokeWidth={2.5} />;
}

function DestinationSimulationCard({
  action,
  data,
  onComplete,
}: {
  action: DemoFlowActionSetAction;
  data: DemoFormData;
  onComplete: () => void;
}) {
  const target = resolveActionTarget(action, data);
  const copy = DESTINATION_COPY[action.destination];
  const showPrefill = action.destination === "whatsapp" && !!action.prefill;

  return (
    <div className="w-full max-w-[300px] self-start rounded-2xl rounded-tl-md border border-background-200 bg-background-50 p-4">
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-500 text-primary-950">
          <DestinationIcon destination={action.destination} />
        </span>
        <div className="min-w-0 leading-tight">
          <span className="block text-sm font-semibold text-foreground-900">{copy.title}</span>
          <span className="text-[11px] text-foreground-500">simülasyon</span>
        </div>
      </div>

      <p className="mt-3 text-xs leading-relaxed text-foreground-600">{copy.description}</p>

      {showPrefill ? (
        <div className="mt-3 rounded-xl border border-background-200 bg-background-100 p-3">
          <span className="block text-[10px] font-semibold uppercase tracking-wide text-foreground-500">
            Gönderilecek mesaj
          </span>
          <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-foreground-900">
            {action.prefill}
          </p>
        </div>
      ) : null}

      <p className="mt-2.5 text-[11px] text-foreground-500">
        <span className="font-medium text-foreground-700">{copy.targetLabel}:</span>{" "}
        {target || "—"}
      </p>

      {!target && (
        <p className="mt-2 text-[11px] font-medium text-accent-700">
          Bu iletişim kanalı için adres henüz tanımlanmamış.
        </p>
      )}

      <p className="mt-2 text-[11px] leading-relaxed text-foreground-400">
        Bu demo sırasında sizi platformda tutuyoruz. Gerçek kurulumda müşteriniz doğrudan
        işletmenizin {copy.channelName} hattına yönlendirilir.
      </p>

      <button
        onClick={onComplete}
        className="mt-4 w-full cursor-pointer whitespace-nowrap rounded-full bg-primary-500 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600"
      >
        Demo Sonuçlarını Gör
      </button>
    </div>
  );
}

function GalleryMessage({ msg }: { msg: ChatMessage }) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);
  const urls = msg.mediaUrls ?? [];
  const title = msg.title || msg.categoryName || "Galeri";

  const scrollBy = (dir: number) => {
    scrollerRef.current?.scrollBy({ left: dir * 200, behavior: "smooth" });
  };

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateArrows();
  }, [urls.length]);

  return (
    <div className="max-w-[92%] self-start">
      <div className="rounded-2xl rounded-tl-md border border-background-200 bg-background-50 p-3">
        <span className="block px-1 text-sm font-semibold text-foreground-900">{title}</span>
        <div className="relative mt-2">
          <div
            ref={scrollerRef}
            onScroll={updateArrows}
            className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {urls.map((u, i) => (
              <div
                key={i}
                className="flex h-44 w-44 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-background-200 bg-background-100"
              >
                <img
                  src={u}
                  alt={`${title} ${i + 1}`}
                  className="h-full w-full object-contain object-top"
                />
              </div>
            ))}
          </div>

          {canLeft && (
            <button
              onClick={() => scrollBy(-1)}
              aria-label="Önceki"
              className="absolute left-1 top-1/2 hidden h-8 w-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-background-300 bg-background-50/90 text-foreground-700 transition hover:bg-background-50 sm:flex"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          {canRight && (
            <button
              onClick={() => scrollBy(1)}
              aria-label="Sonraki"
              className="absolute right-1 top-1/2 hidden h-8 w-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-background-300 bg-background-50/90 text-foreground-700 transition hover:bg-background-50 sm:flex"
            >
              <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
        <p className="mt-1.5 px-1 text-[10px] text-foreground-400">
          {urls.length} görsel • yana kaydırarak inceleyin
        </p>
      </div>
    </div>
  );
}

function CampaignListMessage({ msg }: { msg: ChatMessage }) {
  const campaigns = msg.campaigns ?? [];
  return (
    <div className="max-w-[92%] self-start">
      <div className="rounded-2xl rounded-tl-md border border-background-200 bg-background-50 p-3">
        <div className="flex items-center gap-1.5 px-1">
          <Sparkles className="h-4 w-4 text-accent-500" />
          <span className="text-sm font-semibold text-foreground-900">
            Güncel Kampanyalarımız ✨
          </span>
        </div>
        <div className="mt-2 space-y-2">
          {campaigns.map((c, i) => (
            <div key={i} className="rounded-xl border border-background-200 bg-background-100 p-3">
              <span className="block text-xs font-bold uppercase tracking-wide text-foreground-900">
                {c.name}
              </span>
              <div className="mt-1.5 flex flex-wrap items-baseline gap-2">
                {c.oldPrice ? (
                  <span className="text-xs text-foreground-400 line-through">{c.oldPrice}</span>
                ) : null}
                <span className="text-base font-bold text-accent-600">{c.newPrice}</span>
              </div>
              {c.description ? (
                <p className="mt-1 text-xs leading-relaxed text-foreground-500">{c.description}</p>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}